import { Splide } from '@splidejs/react-splide'
import { Options } from '@splidejs/splide'
import React, { FC, memo, RefObject, useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { Product } from '@Generated/graphql'
import { up } from '@Globals/Style/breakpoints'

import { HorizontalSlider } from './HorizontalSlider'
import { VerticalSlider } from './VerticalSlider'

interface GallerySliderProp extends Pick<Product, 'assets'> {
  onFullScreenToggle?: (fullScreenEnabled: boolean) => void
  title?: string
}

interface StyledGalleryProps {
  disableTransition: boolean
}

const StyledVerticalSlider = styled.div`
  display: none;

  ${up('mobileStyleThreshold')} {
    display: block;
    margin-right: .5rem;
    max-width: 78px;
  }
`

const StyledHorizontalSlider = styled.div<{ isFullscreen: boolean }>(({ isFullscreen }) => css`
  ${!isFullscreen && css`
    width: 100%;
    overflow: hidden;

    ${up('mobileStyleThreshold')} {
      width: 586px;
    }
  `}
`)

const StyledGallery = styled.div<StyledGalleryProps>`
  display: flex;
  width: 100%;
  height: fit-content;

  ${({ disableTransition }) => disableTransition && css`
    ${StyledHorizontalSlider} {
      .splide__list {
        transition: none !important;
      }
    }
  `};
`

const thumbsOptions: Options = {
  autoHeight: true,
  drag: false,
  gap: 10,
  perPage: 5,
  perMove: 5,
  pagination: false,
  rewind: false,
  heightRatio: 5.5,
  isNavigation: false,
  type: 'slide',
}

const mainOptions: Options = {
  isNavigation: true,
  pagination: false,
  easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
  rewind: true,
  type: 'slide',
  drag: true,
  speed: 355,
  updateOnMove: true,
  perMove: 1,
  snap: true,
  flickPower: 110,
  flickMaxPages: 1,
  dragMinThreshold: {
    mouse: 0,
    touch: 10,
  },
}

export const GallerySlider: FC<GallerySliderProp> = memo(({ assets, onFullScreenToggle, title }) => {
  const thumbsRef = useRef<Splide & { hover: boolean }>()
  const mainRef = useRef<Splide>()
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
  const [width, setWidth] = useState(0)
  const [disableTransition, setDisableTransition] = useState(false)

  useEffect(() => {
    const html = (document.getElementsByTagName('BODY') as HTMLCollectionOf<HTMLElement>)[0]
    html.style.overflow = isFullscreen ? 'hidden' : ''
    html.style.position = isFullscreen ? 'fixed' : ''
  }, [isFullscreen])

  const showFullscreen = useCallback(() => {
    const storeElement = document.getElementById('splide02-list')
    const posTest = storeElement?.children[0] as HTMLElement
    setWidth(posTest.offsetWidth)

    setIsFullscreen(true)
    if (onFullScreenToggle) {
      onFullScreenToggle(true)
    }

    const element = document.getElementById('splide02-list')
    if (mainRef?.current?.splide && element) {
      const pos = element.children[0] as HTMLElement
      const offset = window.innerWidth - pos.offsetWidth
      element.style.transform = `translateX(-${(pos.offsetWidth + offset) * mainRef.current.splide.index}px)`
    }
  }, [mainRef, onFullScreenToggle])

  const moveActiveClass = useCallback((index) => {
    if (thumbsRef.current) {
      const { current } = thumbsRef
      if (!current.hover) {
        current.go(index)
      }
      if (current.hover) {
        if (current.splide && mainRef.current?.splide) {
          mainRef.current.splide.options = { speed: 0 }
          mainRef?.current?.go(index)
          const slides = [].slice.call(current.splide.root.getElementsByTagName('UL')[0].children)
          slides.forEach(slide => slide.classList.remove('is-active'))
          slides[index].classList.add('is-active')
          mainRef.current.splide.options = { speed: 400 }
          current.hover = false
        }
      }
    }
  }, [mainRef, thumbsRef])

  const handleMainMove = useCallback((dest, index) => {
    if (assets) {
      const maxIndex = assets.length - 1

      if ((index === maxIndex && dest === 0) || (index === 0 && dest === maxIndex)) {
        setDisableTransition(true)
      } else {
        setDisableTransition(false)
      }
    }

    moveActiveClass(dest)
  }, [assets, moveActiveClass])

  const onHover = useCallback((index) => {
    if (thumbsRef.current) {
      thumbsRef.current.hover = true
      moveActiveClass(index)
    }
  }, [moveActiveClass])

  const onClose = useCallback(() => {
    setIsFullscreen(false)
    if (onFullScreenToggle) {
      onFullScreenToggle(false)
    }
    const element = document.getElementById('splide02-list')

    if (mainRef?.current?.splide && element) {
      element.style.transform = `translateX(-${width * mainRef.current.splide.index}px)`
    }
  }, [mainRef, onFullScreenToggle, width])

  const onMove = useCallback((index) => {
    moveActiveClass(index)
  }, [moveActiveClass])

  useEffect(() => {
    if (!mainRef.current || !thumbsRef.current || !mainRef.current.splide) {
      return
    }

    thumbsRef.current.splide?.on('isOver', onHover)
    mainRef.current.splide?.on('fullscreen', showFullscreen)
    mainRef.current.splide?.on('move', handleMainMove)
  }, [thumbsRef, mainRef, showFullscreen, onHover, handleMainMove])

  return (
    <StyledGallery disableTransition={disableTransition}>
      <StyledVerticalSlider>
        <VerticalSlider
          assets={assets}
          options={thumbsOptions}
          ref={thumbsRef as RefObject<Splide>}
          title={title}
        />
      </StyledVerticalSlider>
      <StyledHorizontalSlider isFullscreen={isFullscreen}>
        <HorizontalSlider
          assets={assets}
          isFullscreen={false}
          onClose={onClose}
          onMove={onMove}
          options={mainOptions}
          ref={mainRef as RefObject<Splide>}
          title={title}
        />
      </StyledHorizontalSlider>
      {isFullscreen && (
        <StyledHorizontalSlider isFullscreen={isFullscreen}>
          <HorizontalSlider
            assets={assets}
            isFullscreen={isFullscreen}
            onClose={onClose}
            onMove={onMove}
            options={mainOptions}
            ref={mainRef as RefObject<Splide>}
            title={title}
          />
        </StyledHorizontalSlider>
      )}
    </StyledGallery>
  )
})
